import { Link } from 'react-router-dom';
import { useDashboardSessionKey } from '../../app/DashboardSessionKeyContext';
import { useOverview } from './useOverview';
import { routingPolicyNamePt } from '../routing/policySchema';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import {
  labelStatusBadgeVariant,
  labelStatusPt,
  providerConnectionStatusBadgeVariant,
  providerConnectionStatusPt,
  providerIdPt,
} from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';

/**
 * `previous === 0` can't produce a meaningful percentage — shown as a plain
 * fact instead of a divide-by-zero artifact ("+Infinity%"/"NaN%"). Either
 * side being `null` (`total_price_cents`'s BigQuery-degraded state, see
 * `@trivio/contracts`' `Overview` docstring) means there's nothing to
 * compare — never rendered as a fabricated "0% vs. mês anterior".
 */
function formatPeriodDelta(current: number | null, previous: number | null): string {
  if (current === null || previous === null) {
    return 'indisponível no momento';
  }
  if (previous === 0) {
    return current === 0 ? 'sem variação vs. mês anterior' : 'sem dado no mês anterior';
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct}% vs. mês anterior`;
}

interface KpiCardProps {
  label: string;
  value: string;
  delta?: string;
}

function KpiCard({ label, value, delta }: KpiCardProps) {
  return (
    <div className="flex flex-col gap-2 border-b border-r border-border p-6">
      <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-3xl font-medium tabular-nums text-foreground">{value}</span>
      {delta ? <span className="font-mono text-xs text-muted-foreground">{delta}</span> : null}
    </div>
  );
}

/**
 * `/` (TTP-282) — dashboard home. Single `GET /overview` call backs every
 * panel here; nothing on this page is computed from a second endpoint or
 * invented for the sake of matching the Claude Design mockup 1:1 — see that
 * issue's "Decisão de escopo" for what was deliberately left out (a
 * routing-adoption percentage chart the backend has no structured data to
 * produce yet).
 */
export function OverviewPage() {
  const { key } = useDashboardSessionKey();
  const apiKey = key?.key ?? null;
  const { overview, loading, error } = useOverview(apiKey);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium text-foreground">Visão geral</h1>
        <p className="text-sm text-muted-foreground">Resumo do mês corrente — cotações, etiquetas, gasto e provedores.</p>
      </div>

      {error ? (
        <Alert variant="error" title="Não foi possível carregar a visão geral">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando visão geral…" />
      ) : overview ? (
        <>
          <div className="grid grid-cols-1 border-l border-t border-border sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label="Cotações"
              value={overview.quotes_count.toLocaleString('pt-BR')}
              delta={formatPeriodDelta(overview.quotes_count, overview.quotes_count_previous_period)}
            />
            <KpiCard
              label="Etiquetas emitidas"
              value={overview.labels_count.toLocaleString('pt-BR')}
              delta={formatPeriodDelta(overview.labels_count, overview.labels_count_previous_period)}
            />
            <KpiCard
              label="Gasto total"
              value={overview.total_price_cents === null ? 'Indisponível' : formatCentsToBRL(overview.total_price_cents)}
              delta={formatPeriodDelta(overview.total_price_cents, overview.total_price_cents_previous_period)}
            />
            <KpiCard
              label="Provedores conectados"
              value={`${overview.providers.filter((p) => p.status === 'active').length}/${overview.providers.length}`}
            />
          </div>

          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_320px]">
            <div className="border border-border">
              <div className="flex items-center justify-between gap-4 border-b border-border p-4">
                <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  Etiquetas recentes
                </span>
                <Link to="/labels" className="font-mono text-xs font-medium text-primary underline-offset-4 hover:underline">
                  Ver todas
                </Link>
              </div>

              {overview.recent_labels.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">Nenhuma etiqueta emitida ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-border text-xs font-medium uppercase tracking-widest text-muted-foreground">
                        <th className="px-4 py-3">Criada em</th>
                        <th className="px-4 py-3">Provedor</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Rastreio</th>
                        <th className="px-4 py-3">Preço</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.recent_labels.map((label) => (
                        <tr key={label.label_id} className="border-b border-border/60 last:border-b-0">
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {new Date(label.created_at).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-3">
                            {providerIdPt(label.provider)} · {label.carrier}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={labelStatusBadgeVariant(label.status)}>{labelStatusPt(label.status)}</Badge>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{label.tracking_code ?? '—'}</td>
                          <td className="px-4 py-3">{formatCentsToBRL(label.price_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4">
              <Card className="flex flex-col">
                <div className="border-b border-border p-4 font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  Provedores
                </div>
                {overview.providers.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">
                    Nenhum provedor conectado ainda —{' '}
                    <Link to="/providers" className="font-medium text-primary underline-offset-4 hover:underline">
                      conecte um
                    </Link>
                    .
                  </p>
                ) : (
                  overview.providers.map((connection) => (
                    <div
                      key={connection.provider}
                      className="flex items-center justify-between gap-3 border-b border-border p-4 last:border-b-0"
                    >
                      <span className="text-sm font-medium text-foreground">{providerIdPt(connection.provider)}</span>
                      <Badge variant={providerConnectionStatusBadgeVariant(connection.status)}>
                        {providerConnectionStatusPt(connection.status)}
                      </Badge>
                    </div>
                  ))
                )}
              </Card>

              <Card className="flex flex-col gap-3 p-4">
                <span className="font-mono text-xs font-medium uppercase tracking-widest text-muted-foreground">Roteamento</span>
                <p className="text-sm text-foreground">
                  Política padrão: <span className="font-medium">{routingPolicyNamePt(overview.routing_policy.default_policy)}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {overview.routing_policy.rules.length === 0
                    ? 'Nenhuma regra configurada — todo envio usa a política padrão.'
                    : `${overview.routing_policy.rules.length} regra${overview.routing_policy.rules.length > 1 ? 's' : ''} configurada${overview.routing_policy.rules.length > 1 ? 's' : ''}.`}
                </p>
                <Link to="/routing" className="text-sm font-medium text-primary underline-offset-4 hover:underline">
                  Configurar roteamento
                </Link>
              </Card>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
